import { Accordion, Box, Input, Stack, TextInput } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { showOldNotification as showNotification } from "@rpi/openapi-core";
import { GraphQL, Rest } from "@rpi/openapi-api";
import { tokens } from "@blurb/codex-react";
import React from "react";
import ReactJson from "react-json-view";
import {
  ApiAccordion,
  ApiAccordionForm,
  ApiAccordionLabel,
  ApiMethod,
} from "../../../components/accordions/ApiAccordion";
import { SelectApiCredential } from "../../../components/selects/SelectApiCredential";
import { SelectCustomerProduct } from "../../../components/selects/SelectCustomerProduct";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { formatAuthHeader, generateJsonCurl, propPlaceholder } from "../../../utils/misc.util";
import {
  deriveOrderFiles,
  fetchTemplateMetadata,
  resolveProductLabel,
  templateApiBaseUrl,
} from "../../../utils/templateApi.util";
import { isEmptyString, isRecord } from "../../../utils/type.util";
import {
  applyFileUrlsToFirstOrderItem,
  editableFileUrl,
  isSelectedSku,
  type ProductFileUrls,
  readProductFileUrls,
  selectCredentialsForEnvironment,
  sortProductsForSelect,
  UNRESOLVED_FILE_URLS,
  unsetFileUrls,
} from "./apiExplorerForm";
import { API_EXPLORER_ENDPOINTS, createOrderExampleBody, PLACEHOLDER } from "./apiExplorerSpec";
import { useApiRequest } from "./useApiRequest";

type ApiCredential = GraphQL.ListApiCredentialsQuery["listApiCredentials"][number];
type CustomerProduct = GraphQL.ListCustomerProductsQuery["listCustomerProducts"][number];

interface GetOrderFormProps {
  apiCredentials?: ApiCredential[];
}

interface CreateOrderFormProps {
  apiCredentials?: ApiCredential[];
  customerProducts?: CustomerProduct[];
}

/**
 * API Explorer tab: an accordion of the Get Order and Create Order endpoints, each expanding to a
 * request form that runs against the caller's selected credential.
 *
 * Submitting Create Order creates a real order — there is no dry run — so both forms offer the keys
 * for the session's environment and nothing else: a production session creates production orders, a
 * sandbox session sandbox ones.
 */
export const ApiExplorerTab: React.FC = () => {
  const user = useAuthUser();
  const apiCredentialsQuery = GraphQL.useListApiCredentialsQuery();
  const customerProductsQuery = GraphQL.useListCustomerProductsQuery();

  const allCredentials = apiCredentialsQuery.data?.listApiCredentials;
  // Memoized so the credential pickers keep the same array identity across renders.
  const apiCredentials = React.useMemo(
    () => selectCredentialsForEnvironment(allCredentials, user.isSandbox),
    [allCredentials, user.isSandbox]
  );
  const customerProducts = customerProductsQuery.data?.listCustomerProducts;

  return (
    <Stack spacing="md" pt="md" data-testid="api-explorer-tab">
      <ApiAccordion chevronPosition="right">
        <Accordion.Item value="get-order">
          <Accordion.Control>
            <ApiAccordionLabel
              method={ApiMethod.GET}
              path={API_EXPLORER_ENDPOINTS.getOrder.path}
              description={API_EXPLORER_ENDPOINTS.getOrder.description}
            />
          </Accordion.Control>
          <Accordion.Panel>
            <GetOrderForm apiCredentials={apiCredentials} />
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="create-order">
          <Accordion.Control>
            <ApiAccordionLabel
              method={ApiMethod.POST}
              path={API_EXPLORER_ENDPOINTS.createOrder.path}
              description={API_EXPLORER_ENDPOINTS.createOrder.description}
            />
          </Accordion.Control>
          <Accordion.Panel>
            <CreateOrderForm apiCredentials={apiCredentials} customerProducts={customerProducts} />
          </Accordion.Panel>
        </Accordion.Item>
      </ApiAccordion>
    </Stack>
  );
};

function useSelectedCredential(apiCredentials?: ApiCredential[]) {
  const [selectedKey, setSelectedKey] = React.useState<string>("");
  const selectedCredential = apiCredentials?.find((credential) => credential.apiKey === selectedKey);
  const authHeader = selectedCredential
    ? formatAuthHeader(selectedCredential.apiKey, selectedCredential.sharedSecret)
    : "";
  return { selectedApiKey: selectedCredential?.apiKey ?? "", setSelectedKey, authHeader };
}

const printApiBaseUrl = import.meta.env.VITE_PUBLIC_PRINTAPI_ENDPOINT_URL;
const templateBaseUrl = templateApiBaseUrl();
const authHeaderPlaceholder = "Basic base64(apiKey:sharedSecret)";

export function GetOrderForm({ apiCredentials }: GetOrderFormProps) {
  const [orderId, setOrderId] = React.useState("");
  const { selectedApiKey, setSelectedKey, authHeader } = useSelectedCredential(apiCredentials);
  const { isLoading, response, send } = useApiRequest<unknown>();

  // Encoded to match what getOrderById actually requests, so the curl stays copy-pasteable.
  const orderIdSegment = isEmptyString(orderId) ? ":customerOrderId" : encodeURIComponent(orderId);

  const curl = React.useMemo(
    () =>
      generateJsonCurl({
        method: ApiMethod.GET,
        url: `${printApiBaseUrl}/orders/${orderIdSegment}`,
        headers: { accept: "application/json", Authorization: propPlaceholder(authHeader, authHeaderPlaceholder) },
      }),
    [authHeader, orderIdSegment]
  );

  const handleSubmit = React.useCallback(() => {
    send(
      () => Rest.getOrderById(orderId, authHeader),
      () => showNotification({ title: "Fetched order.", message: `Got order '${orderId}'.`, color: "green" })
    );
  }, [orderId, authHeader, send]);

  const isMissingRequiredInput = isEmptyString(authHeader) || isEmptyString(orderId);

  return (
    <ApiAccordionForm
      curl={curl}
      response={response}
      buttonProps={{ loading: isLoading, disabled: isMissingRequiredInput, onClick: handleSubmit }}
    >
      <SelectApiCredential
        required
        data={apiCredentials}
        value={selectedApiKey}
        onChange={(v) => setSelectedKey(v ?? "")}
      />
      <TextInput
        label="Customer Order ID"
        placeholder="Enter"
        value={orderId}
        onChange={(e) => setOrderId(e.currentTarget.value)}
      />
    </ApiAccordionForm>
  );
}

export function CreateOrderForm({ apiCredentials, customerProducts }: CreateOrderFormProps) {
  const { selectedApiKey, setSelectedKey, authHeader } = useSelectedCredential(apiCredentials);
  const { isLoading, response, send } = useApiRequest<Rest.CreateOrderResponse>();
  const [customerSku, setCustomerSku] = React.useState<string>(PLACEHOLDER.sku);

  // Seeded from the published create-order example (see apiExplorerSpec) rather than assembled here.
  const [data, setData] = React.useState<Record<string, unknown>>(() => structuredClone(createOrderExampleBody));

  const selectedProduct = customerProducts?.find((product) => product.customerSku === customerSku);
  const manufacturingSku = selectedProduct?.openapiProduct?.openapiSku;
  const productLabel = resolveProductLabel(selectedProduct?.name, selectedProduct?.description);

  const setProductFiles = (fileUrls: Partial<ProductFileUrls>, sku?: string) =>
    setData((previous) => applyFileUrlsToFirstOrderItem(previous, fileUrls, sku));

  // Emptying a file field restores its placeholder token, so the Data body always names what is
  // still missing instead of carrying an empty string into the request.
  const editPagesUrl = (url: string) => setProductFiles({ gutsUrl: url || PLACEHOLDER.gutsUrl });
  const editCoverUrl = (url: string) => setProductFiles({ coverUrl: url || PLACEHOLDER.coverUrl });

  // A new product invalidates the files already in the body: they belong to the previous product.
  const selectProduct = (value: string | null) => {
    const sku = value || "";
    setCustomerSku(sku);
    setProductFiles(UNRESOLVED_FILE_URLS, sku);
  };

  const editData = ({ updated_src }: { updated_src: object }) => {
    if (isRecord(updated_src)) {
      setData(updated_src);
    }
  };

  // Cover requires a page count; the template metadata endpoint supplies the product's files.
  useQuery(
    ["template-metadata", manufacturingSku],
    () => fetchTemplateMetadata(templateBaseUrl ?? "", manufacturingSku ?? ""),
    {
      enabled: !!manufacturingSku && !!templateBaseUrl,
      onSuccess: (metadata) => {
        if (!manufacturingSku || !templateBaseUrl) return;
        const files = deriveOrderFiles({ base: templateBaseUrl, manufacturingSku, label: productLabel, metadata });
        setData((previous) => applyFileUrlsToFirstOrderItem(previous, unsetFileUrls(previous, files)));
      },
      onError: () =>
        showNotification({
          title: "Failed to load template files.",
          message: "Could not fetch the product's template metadata; cover and pages URLs were left unset.",
          color: "red",
        }),
    }
  );

  const { coverUrl, gutsUrl } = readProductFileUrls(data);
  const coverField = editableFileUrl(coverUrl);
  const pagesField = editableFileUrl(gutsUrl);
  const productOptions = React.useMemo(() => sortProductsForSelect(customerProducts), [customerProducts]);

  const curl = React.useMemo(
    () =>
      generateJsonCurl({
        method: ApiMethod.POST,
        url: `${printApiBaseUrl}/orders/create`,
        headers: { accept: "application/json", Authorization: propPlaceholder(authHeader, authHeaderPlaceholder) },
        data,
      }),
    [authHeader, data]
  );

  const handleSubmit = React.useCallback(() => {
    send(
      () => Rest.createOrderFromJsonBody(data, authHeader),
      (created) =>
        showNotification({
          title: "Order created.",
          message: `Created order '${created.customerOrderId}'.`,
          color: "green",
        })
    );
  }, [data, authHeader, send]);

  // Every input the form marks required has to be supplied before Submit can create a real order, and
  // a placeholder token is neither a product nor a PDF. The file fields already read empty while the
  // body holds their tokens, so this gates on exactly what the caller sees.
  const isMissingRequiredInput =
    isEmptyString(authHeader) || !isSelectedSku(customerSku) || isEmptyString(coverField) || isEmptyString(pagesField);

  return (
    <ApiAccordionForm
      curl={curl}
      response={response}
      buttonProps={{ loading: isLoading, disabled: isMissingRequiredInput, onClick: handleSubmit }}
      jsonInput={
        <Input.Wrapper label="Data" required>
          <Box
            p={8}
            sx={{
              maxHeight: 400,
              overflowY: "auto",
              border: `1px solid ${tokens.color.semantic.border.faint}`,
              borderRadius: 4,
            }}
          >
            <ReactJson
              src={data}
              onEdit={editData}
              onAdd={editData}
              onDelete={editData}
              name={null}
              displayDataTypes={false}
              style={{ fontSize: 14 }}
            />
          </Box>
        </Input.Wrapper>
      }
    >
      <SelectApiCredential
        required
        data={apiCredentials}
        value={selectedApiKey}
        onChange={(v) => setSelectedKey(v ?? "")}
      />
      <SelectCustomerProduct required data={productOptions} value={customerSku} onChange={selectProduct} />
      <TextInput
        required
        label="Cover PDF"
        placeholder={PLACEHOLDER.coverUrl}
        value={coverField}
        onChange={(e) => editCoverUrl(e.currentTarget.value)}
        data-testid="cover-pdf-url"
      />
      <TextInput
        required
        label="Pages PDF"
        placeholder={PLACEHOLDER.gutsUrl}
        value={pagesField}
        onChange={(e) => editPagesUrl(e.currentTarget.value)}
        data-testid="pages-pdf-url"
      />
    </ApiAccordionForm>
  );
}
